import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { DateTime } from 'luxon';

import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { AuthService } from '@shared/authentication/auth.service';
import { SitesService } from '@shared/services/sites.service';
import { BrowserProviderService } from '@shared/services/browser-provider.service';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';

import { Site } from '@shared/models/site';
import { SiteConfig } from '@shared/models/site-config';
import { Shift } from '@shared/models/shift';
import { WorkedShift } from '@shared/models/worked-shift';

import { ShiftContext } from '../models/shift-context';

export type ShiftContextSelection = { siteId: string, date: DateTime, shiftId: number, shiftNum: number };
@Injectable({
    providedIn: 'root'
})
export class ShiftContextService {
    private static readonly LOCAL_STORAGE_KEY: string = '_vision_context';

    private _subscriptions: Subscription[] = [];

    private username: string;

    private _sites = new BehaviorSubject<Site[]>(null);
    private _shifts = new BehaviorSubject<Shift[]>(null);

    PermittedSites: Observable<Site[]> = this._sites.asObservable().pipe(filter(sites => sites !== null));

    SiteShifts: Observable<Shift[]> = this._shifts.asObservable().pipe(filter(shifts => shifts !== null));

    private _context = new BehaviorSubject<ShiftContext>(null);

    CurrentContext: Observable<ShiftContext> = this._context.pipe(
        distinctUntilChanged((prev, cur) => {
            return prev?.id === cur?.id
                && prev?.site.id === cur?.site.id
                && prev?.date.equals(cur?.date)
                && prev?.shift.id === cur?.shift.id
                && prev?.status === cur?.status;
        }),
        filter( c => !!c ),
        shareReplay(1));

    private _selection = new BehaviorSubject<ShiftContextSelection>(null);

    // Only use this if you know what you're doing. the combinations returned by this may not reflect actual open
    // shifts, and this should only be used in specific cases (currently only piece rate form)
    ImmediateSelection: Observable<ShiftContextSelection> = this._selection.pipe(filter( c => !!c ));

    private _workedShiftId = new BehaviorSubject<number | null>(null);

    // prefer CurrentContext
    get WorkedShift(): Observable<WorkedShift> {
        return this._workedShiftId.pipe(
            distinctUntilChanged(),
            filter(id => !!id),
            switchMap(id => this.workedShiftService.FollowById(id)));
    };

    private _siteConfig = new BehaviorSubject<SiteConfig[]>([]);

    SiteConfig: Observable<SiteConfig[]> = this._siteConfig.asObservable();

    constructor(private sitesService: SitesService,
                private authService: AuthService,
                private browserService: BrowserProviderService,
                private activityService: ActivityBlockingService,
                private workedShiftService: WorkedShiftsService) {

        this._subscriptions = [
            this._workedShiftId
                .pipe(filter(id => !!id))
                .subscribe(contextId => {
                    // save current context to local storage
                    this.StoreContext(contextId);
                }),

            this._selection
                .pipe(
                    distinctUntilChanged((prev, cur) =>
                        prev?.siteId === cur?.siteId
                        && prev?.date.equals(cur?.date)
                        && prev?.shiftId === cur?.shiftId),
                    startWith(null))
                .subscribe(selection => {
                    // we call every change, but the site service caches the result for us
                    if (selection?.siteId) {
                        this.GetNewConfig(selection.siteId);
                        this.UpdateShifts(selection.siteId);
                    }
                }),

            combineLatest([this.WorkedShift, this.PermittedSites, this.SiteShifts])
                .subscribe(([workedShift, sites, shifts]) => {
                    if (!workedShift)
                        return null;

                    const site = sites.find(s => s.id === workedShift.siteId);
                    const shift = shifts.find(s => s.id === workedShift.shiftId);

                    if (!site || !shift)
                        return null;

                    this._context.next({
                        id: workedShift.id,
                        site,
                        date: workedShift.date,
                        shift,
                        status: workedShift.shiftStatus
                    });
                }),

            combineLatest([this.sitesService.GetWarehousingSites(), this.authService.getUsername()])
                .pipe(
                    this.activityService.Wait('shift context setup'),
                    switchMap(([sites, username]) => {
                        this._sites.next(sites);

                        this.username = username ?? '';

                        const stored = this.GetStoredContextId();

                        if (stored) {
                            return this.UpdateSelection({ id: stored })
                                .pipe(catchError(_ => {
                                    this.browserService.localStorage
                                        .removeItem(this.username + ShiftContextService.LOCAL_STORAGE_KEY);

                                    return this.workedShiftService.GetLatestForSite(sites[0].id)
                                        .pipe(switchMap(ws => this.UpdateSelection(ws)));
                                }));
                        }
                        else {
                            return this.workedShiftService.GetLatestForSite(sites[0].id)
                                .pipe(switchMap(ws => this.UpdateSelection(ws)));
                        }
                    }))
                .subscribe()
        ];
    }

    UpdateSelection(val: WorkedShift | { id: number } | ShiftContextSelection): Observable<WorkedShift> {
        if ('id' in val) {
            return this.workedShiftService.GetImmediateById(val.id)
                .pipe(tap(ws => {
                    this._workedShiftId.next(ws.id);
                    this._selection.next({ siteId: ws.siteId, date: ws.date, shiftId: ws.shiftId, shiftNum: ws.shift.number });
                }));
        }
        else {
            return this.workedShiftService.GetByDateShift(val.siteId, val.date, val.shiftId)
                .pipe(tap(ws => {
                    this._workedShiftId.next(ws?.id ?? null); // null pushes old value out of BehaviorSubject in case we go back to it and need to refresh
                    this._selection.next(val);
                }));
        }
    }

    private GetStoredContextId(): number | null {
        const val = this.browserService.localStorage
            .getItem(this.username + ShiftContextService.LOCAL_STORAGE_KEY);

        if (!val)
            return null;

        try {
            return JSON.parse(val).id || null;
        }
        catch (error) {
            this.browserService.localStorage
                .removeItem(this.username + ShiftContextService.LOCAL_STORAGE_KEY);
            return null;
        }
    }

    private StoreContext(contextId: number): void {
        if (!contextId)
            return;

        const val = JSON.stringify({ id: contextId });

        this.browserService.localStorage.setItem(this.username + ShiftContextService.LOCAL_STORAGE_KEY, val);
    }

    private UpdateShifts(siteId: string): void {
        const handle = this.activityService.StartWorking('shift context loading shift');
        this.sitesService.GetShiftsForSite(siteId).subscribe(shifts => {
            this._shifts.next(shifts);
            this.activityService.FinishWorking(handle);
        });
    }

    // Context Manipulation /////////////////////////////////////////////////////////////////

    OpenShift(siteId: string, date: DateTime, shiftId: number): Observable<WorkedShift> {
        return this.workedShiftService.OpenShift(siteId, date, shiftId).pipe(switchMap(workedShift => {
            return this.UpdateSelection(workedShift);
        }));
    }

    // Site Configs /////////////////////////////////////////////////////////////////////

    private GetNewConfig(siteId: string): void {
        this.sitesService.GetSiteConfigs(siteId).subscribe(config => {
            this._siteConfig.next(config);
        });
    }

    SetConfig(key: string, value: string): Observable<void> {
        const siteId = this._selection.value.siteId;
        return this.sitesService.SetSiteConfig(siteId, key, value).pipe(tap(() => {
            this.GetNewConfig(siteId);
        }));
    }
}
